import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PackagingApiAdapter } from '../adapters/PackagingApiAdapter';
import { BoxGroupItem } from '../models/Packaging';

interface CreatePackagingBoxGroupPayload {
  name: string;
  teamId: number;
}

export const useCreatePackagingBoxGroup = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('packagingModule');
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackagingApiAdapter(httpClient);

  const mutation = useMutation({
    mutationFn: async ({ name, teamId }: CreatePackagingBoxGroupPayload) => {
      const trimmedName = name.trim();
      if (!trimmedName) {
        throw new Error(t('errors.box_group_name_required'));
      }

      return apiAdapter.createBoxGroup({
        name: trimmedName,
        team_id: teamId,
        box_category: 'customer_box',
      });
    },
    onSuccess: (createdGroup: BoxGroupItem, payload) => {
      queryClient.invalidateQueries({ queryKey: ['packaging-box-groups'] });
      queryClient.invalidateQueries({
        queryKey: ['packaging-box-groups', payload.teamId, 'customer_box'],
      });

      toast({
        title: t('messages.box_group_create_success'),
        description: t('messages.box_group_create_success_description', { name: createdGroup.name }),
        variant: 'default',
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || t('errors.unexpected');
      toast({
        title: t('messages.box_group_create_error'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  return {
    createBoxGroupAsync: mutation.mutateAsync,
    isCreatingBoxGroup: mutation.isPending,
  };
};
